import React from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import useThemeStore from '../store/useThemeStore';

const styles = {
  container: {
    position: 'relative',
    display: 'inline-block',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    borderRadius: '8px',
    border: '1px solid var(--border)',
    backgroundColor: 'var(--bg)',
    color: 'var(--text)',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  buttonHover: {
    backgroundColor: 'var(--bgSecondary)',
    borderColor: 'var(--borderHover)',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '0.5rem',
    minWidth: '180px',
    backgroundColor: 'var(--bg)',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    boxShadow: 'var(--shadowLg)',
    overflow: 'hidden',
    zIndex: 50,
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    fontSize: '0.875rem',
    color: 'var(--text)',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    border: 'none',
    backgroundColor: 'transparent',
    width: '100%',
    textAlign: 'left',
  },
  optionHover: {
    backgroundColor: 'var(--bgSecondary)',
  },
  optionActive: {
    backgroundColor: 'var(--primaryBg)',
    color: 'var(--primary)',
  },
  optionIcon: {
    width: '18px',
    height: '18px',
  },
  optionText: {
    flex: 1,
  },
  checkmark: {
    width: '16px',
    height: '16px',
    color: 'var(--primary)',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 40,
  },
};

const themeOptions = [
  { value: 'light', label: 'Claro', icon: Sun },
  { value: 'dark', label: 'Oscuro', icon: Moon },
  { value: 'system', label: 'Sistema', icon: Monitor },
];

export default function ThemeToggle({ showLabel = false, variant = 'default' }) {
  const { theme, setTheme, getActualTheme } = useThemeStore();
  const [isOpen, setIsOpen] = React.useState(false);
  const [hoveredButton, setHoveredButton] = React.useState(false);
  const [hoveredOption, setHoveredOption] = React.useState(null);

  const actualTheme = getActualTheme();
  const CurrentIcon = actualTheme === 'dark' ? Moon : Sun;

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    setIsOpen(false);
  };

  React.useEffect(() => {
    // Re-initialize theme when component mounts
    const { initializeTheme } = useThemeStore.getState();
    const cleanup = initializeTheme();
    return cleanup;
  }, []);

  const buttonStyle = variant === 'minimal' ? {
    ...styles.button,
    padding: '0.5rem',
    border: 'none',
    backgroundColor: 'transparent',
  } : styles.button;

  return (
    <div style={styles.container}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setHoveredButton(true)}
        onMouseLeave={() => setHoveredButton(false)}
        style={{
          ...buttonStyle,
          ...(hoveredButton && variant !== 'minimal' ? styles.buttonHover : {}),
        }}
        aria-label="Cambiar tema"
      >
        <div style={styles.iconContainer}>
          <CurrentIcon size={18} />
        </div>
        {showLabel && (
          <span>Tema</span>
        )}
      </button>

      {isOpen && (
        <>
          <div 
            style={styles.overlay} 
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <div style={styles.dropdown}>
            {themeOptions.map((option) => {
              const Icon = option.icon;
              const isActive = theme === option.value;
              const isHovered = hoveredOption === option.value;

              return (
                <button
                  key={option.value}
                  onClick={() => handleThemeChange(option.value)}
                  onMouseEnter={() => setHoveredOption(option.value)}
                  onMouseLeave={() => setHoveredOption(null)}
                  style={{
                    ...styles.option,
                    ...(isHovered ? styles.optionHover : {}),
                    ...(isActive ? styles.optionActive : {}),
                  }}
                >
                  <Icon size={18} style={styles.optionIcon} />
                  <span style={styles.optionText}>{option.label}</span>
                  {isActive && (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                      style={styles.checkmark}
                    >
                      <path d="M13.78 3.22a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 0 1-1.06 0l-3-3a.75.75 0 1 1 1.06-1.06L6 10.44l6.97-6.97a.75.75 0 0 1 1.06 0z" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}